import Phaser from "phaser";
import type { EventBus } from "../engine/EventBus";
import { AudioManager } from "../engine/AudioManager";
import type { Rollback } from "../engine/Rollback";
import type { Blackboard } from "../engine/Blackboard";
import type { Interpreter } from "../engine/Interpreter";
import { MinigameScene } from "./MinigameScene";
import { placeObjects, type Shelf } from "../engine/ObjectPlacement";
import { resolveAssetUrl } from "../engine/assetRoot";

const POSITIONS: Record<string, { x: number; y: number }> = {
  far_left: { x: 192, y: 720 },
  left: { x: 384, y: 720 },
  center: { x: 640, y: 720 },
  right: { x: 896, y: 720 },
  far_right: { x: 1088, y: 720 },
};

const TRANSITION_MS = 500;
const OBJECT_PIN_SCALE = 0.5;   // in-scene object sprites render at half their authored (full) size
const PRESENT_MS = 320;         // featured-presentation enter (zoom-in)
const PRESENT_EXIT_MS = 340;    // exit ~ same length, so the fade/settle reads clearly
const PRESENT_DIM_ALPHA = 0.5;  // "dim everything else to ~50%"
const PRESENT_TARGET_H = 0.5;   // featured object fills ~50% of frame height

/**
 * Named compositing layers as Phaser depth bands — see
 * design_docs/arctree_compositing_and_layering_spec_06JUN26.md §2.
 * Rendered bottom → top. The `ui` layer (React DOM above the canvas) is not a
 * Phaser depth and so is not represented here. The object/base/overlay bands are
 * scaffolded in Phase 1a and populated by later phases (objects: P4, base: P2,
 * overlay: P3); only BACKGROUND, CHARACTERS and EFFECTS carry content today.
 */
const LAYERS = {
  BASE: -20,         // animated/scrolling background behind everything (P2)
  BACKGROUND: -10,   // primary scene image
  OBJECTS_BACK: -5,  // object sprites rendered behind characters (P4)
  CHARACTERS: 0,     // character sprites; author %z% offsets within this band
  OBJECTS_FRONT: 50, // object sprites rendered in front of characters (P4, default)
  OVERLAY: 60,       // atmospheric overlays: weather / tint / color wash (P3)
  EFFECTS: 100,      // full-frame transition & flash overlays (above all scene content)
} as const;

// Author %z% is offset into the CHARACTERS band and clamped so it can never
// cross into the object bands above/below. With CHARACTERS = 0 the common case
// (z = 0) is unchanged from the prior behaviour.
const CHARACTER_Z_MAX = LAYERS.OBJECTS_FRONT - LAYERS.CHARACTERS - 1; // 49
const clampCharacterZ = (z: number): number =>
  Math.max(0, Math.min(CHARACTER_Z_MAX, z));

export class GameScene extends Phaser.Scene {
  private bus!: EventBus;
  private _rollback: Rollback | null = null;
  private _unsubs: Array<() => void> = [];
  private _bg: Phaser.GameObjects.Image | null = null;
  private _base: Phaser.GameObjects.TileSprite | Phaser.GameObjects.Image | Phaser.GameObjects.Video | null = null;
  private _baseScroll: { dir: string; speed: number } | null = null;
  private _sprites: Map<string, Phaser.GameObjects.Image> = new Map();
  private _objects: Map<string, Phaser.GameObjects.Image> = new Map();
  private _presentation: {
    sprite: Phaser.GameObjects.Image;
    dim: Phaser.GameObjects.Rectangle;
    exit: string;
  } | null = null;
  private _loadQueue: Map<string, Array<() => void>> = new Map();
  private _audio: AudioManager | null = null;
  private _qKey: Phaser.Input.Keyboard.Key | null = null;
  private _effectOverlay: Phaser.GameObjects.Rectangle | null = null;
  private _animationQueue: Map<string, { anim: string; duration: number | undefined }[]> = new Map();
  private _bb: Blackboard | null = null;
  private _minigameActive = false;

  constructor() {
    super({ key: "GameScene" });
  }

  init(data: {
    bus: EventBus;
    interpreter?: Interpreter;
    bb?: Blackboard;
    rollback?: Rollback;
  }) {
    this.bus = data.bus;
    this._bb = data.bb ?? null;
    this._rollback = data.rollback ?? null;
  }

  create() {
    this._unsubs.push(
      this.bus.on("game.reset", () => this._onGameReset()),
      this.bus.on("bg.set", (d) => {
        this._onBgSet(d.asset, d.transition, d.duration, d.transition_color);
      }),
      this.bus.on("base.set", (d) => {
        this._onBaseSet(d.asset, d.mode, d.scroll, d.speed, d.opacity, d.loop);
      }),
      this.bus.on("base.clear", () => this._onBaseClear()),
      this.bus.on("object.show", (d) => this._onObjectShow(d.id, d.asset, d.x, d.y, d.layer)),
      this.bus.on("object.update", (d) => this._onObjectUpdate(d.id, d.asset)),
      this.bus.on("object.hide", (d) => this._onObjectHide(d.id)),
      this.bus.on("object.present", (d) => this._onObjectPresent(d.id, d.asset, d.exit)),
      this.bus.on("object.room", (d) => this._onObjectRoom(d.items, d.shelves)),
      this.bus.on("advance", () => this._dismissPresentation()),
      this.bus.on("sprite.show", (d) => {
        this._onSpriteShow(d.id, d.asset, d.pos, d.enter, d.z, d.scale);
      }),
      this.bus.on("sprite.hide", (d) => this._onSpriteHide(d.id, d.exit, d.duration)),
      this.bus.on("effect.run", (d) => this._onEffect(d.effect, d.intensity, d.duration, d.color)),
      this.bus.on("animation.run", (d) => this._onAnimation(d.id, d.anim, d.duration)),
    );

    this._unsubs.push(
      this.bus.on("minigame.start", (d) => this._onMinigameStart(d.id, d.config)),
      this.bus.on("minigame.complete", () => this._onMinigameComplete()),
    );

    if (!this.scene.get("MinigameScene")) {
      this.scene.add("MinigameScene", MinigameScene, false);
    }

    this._audio = new AudioManager(this, this.bus);
    this.bus.emit("scene.ready");

    if (this._rollback) {
      this._rollback.setAudioAdapter(this._audio);
    }

    if (this.input.keyboard) {
      this._qKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.Q);
      this._qKey.on("down", () => {
        if (this._minigameActive) return;
        if (this._rollback?.canRewind()) {
          this._rollback.rewind();
        }
      });
    }

  }

  getAudioManager(): AudioManager | null {
    return this._audio;
  }

  private _ensureLoaded(key: string, cb: () => void) {
    if (this.textures.exists(key)) {
      cb();
      return;
    }

    if (this._loadQueue.has(key)) {
      this._loadQueue.get(key)!.push(cb);
      return;
    }

    this._loadQueue.set(key, [cb]);
    this.load.image(key, resolveAssetUrl(key));
    this.load.once(`filecomplete-image-${key}`, () => {
      const cbs = this._loadQueue.get(key) ?? [];
      this._loadQueue.delete(key);
      for (const fn of cbs) fn();
    });
    this.load.once("loaderror", (file: Phaser.Loader.File) => {
      if (file.key === key) {
        console.warn(`[GameScene] LOAD ERROR for "${key}"`);
        this._loadQueue.delete(key);
      }
    });
    this.load.start();
  }

  private _onGameReset(): void {
    if (this._bg) { this._bg.destroy(); this._bg = null; }
    this._onBaseClear();
    for (const s of this._sprites.values()) s.destroy();
    this._sprites.clear();
    this._clearObjects();
    this._loadQueue.clear();
    this._animationQueue.clear();
    if (this._effectOverlay) { this._effectOverlay.destroy(); this._effectOverlay = null; }
    if (this._audio) this._audio.stopAll();
  }

  private _onBgSet(asset: string, transition?: string, duration?: number, transitionColor?: string) {
    this._finishPresentation(true); // a scene change clears any active featured presentation
    const ms = (duration ?? 0.5) * 1000;

    this._ensureLoaded(asset, () => {
      const oldBg = this._bg;
      const newBg = this.add.image(640, 360, asset).setOrigin(0.5).setDepth(LAYERS.BACKGROUND);
      this._coverFit(newBg);

      if (transition === "dissolve" && oldBg) {
        newBg.setAlpha(0);
        this.tweens.add({
          targets: newBg,
          alpha: 1,
          duration: ms,
          onComplete: () => oldBg.destroy(),
        });
      } else if (transition === "fade_in") {
        newBg.setAlpha(0);
        if (oldBg) oldBg.destroy();
        this.tweens.add({
          targets: newBg,
          alpha: 1,
          duration: ms,
        });
      } else if (transition === "wipe_right") {
        newBg.setX(640 + 1280);
        this.tweens.add({
          targets: newBg, x: 640, duration: ms, ease: "Linear",
          onComplete: () => { if (oldBg) oldBg.destroy(); },
        });
      } else if (transition === "wipe_left") {
        newBg.setX(640 - 1280);
        this.tweens.add({
          targets: newBg, x: 640, duration: ms, ease: "Linear",
          onComplete: () => { if (oldBg) oldBg.destroy(); },
        });
      } else if (transition === "wipe_up") {
        newBg.setY(360 + 720);
        this.tweens.add({
          targets: newBg, y: 360, duration: ms, ease: "Linear",
          onComplete: () => { if (oldBg) oldBg.destroy(); },
        });
      } else if (transition === "wipe_down") {
        newBg.setY(360 - 720);
        this.tweens.add({
          targets: newBg, y: 360, duration: ms, ease: "Linear",
          onComplete: () => { if (oldBg) oldBg.destroy(); },
        });
      } else if (transition === "fade_white") {
        this._fadeThrough(0xffffff, ms, oldBg, newBg);
      } else if (transition === "fade_color" && transitionColor) {
        const color = parseInt(transitionColor.replace("#", ""), 16);
        this._fadeThrough(color, ms, oldBg, newBg);
      } else {
        if (oldBg) oldBg.destroy();
      }

      this._bg = newBg;
    });
  }

  private _fadeThrough(
    color: number,
    ms: number,
    oldBg: Phaser.GameObjects.Image | null,
    newBg: Phaser.GameObjects.Image
  ): void {
    newBg.setAlpha(0);
    // fillAlpha=1, GameObject alpha=0: rendered opacity is fillAlpha*alpha, so we
    // drive visibility via the tweened alpha (0->1->0). Creating it with fillAlpha=0
    // would pin the product at 0 and the overlay would never appear.
    const overlay = this.add.rectangle(640, 360, 1280, 720, color, 1).setDepth(LAYERS.EFFECTS).setAlpha(0);
    this.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: ms / 2,
      onComplete: () => {
        if (oldBg) oldBg.destroy();
        newBg.setAlpha(1);
        this.tweens.add({
          targets: overlay,
          alpha: 0,
          duration: ms / 2,
          onComplete: () => overlay.destroy(),
        });
      },
    });
  }

  private _onBaseClear(): void {
    if (this._base) {
      this._base.destroy();
      this._base = null;
    }
    this._baseScroll = null;
  }

  private _onBaseSet(
    asset: string,
    mode?: string,
    scroll?: string,
    speed?: number,
    opacity?: number,
    loop?: boolean,
  ): void {
    this._onBaseClear();
    if (!asset) return;
    const alpha = opacity ?? 1;

    if (mode === "video") {
      this._createBaseVideo(asset, alpha, loop ?? true);
      return;
    }

    this._ensureLoaded(asset, () => {
      if (mode === "scroll") {
        const ts = this.add
          .tileSprite(640, 360, 1280, 720, asset)
          .setDepth(LAYERS.BASE)
          .setAlpha(alpha);
        this._base = ts;
        this._baseScroll = { dir: scroll ?? "horizontal", speed: speed ?? 30 };
      } else {
        const img = this.add.image(640, 360, asset).setOrigin(0.5).setDepth(LAYERS.BASE).setAlpha(alpha);
        this._coverFit(img);
        this._base = img;
      }
    });
  }

  private _createBaseVideo(asset: string, alpha: number, loop: boolean): void {
    const create = () => {
      try {
        const vid = this.add.video(640, 360, asset).setDepth(LAYERS.BASE).setAlpha(alpha);
        vid.setMute(true);
        vid.on("play", () => this._coverFitVideo(vid));
        vid.play(loop);
        this._coverFitVideo(vid);
        this._base = vid;
      } catch (e) {
        console.warn(`[GameScene] base video failed: "${asset}"`, e);
      }
    };
    if (this.cache.video.exists(asset)) {
      create();
      return;
    }
    this.load.video(asset, resolveAssetUrl(asset));
    this.load.once(`filecomplete-video-${asset}`, create);
    this.load.once("loaderror", (file: Phaser.Loader.File) => {
      if (file.key === asset) console.warn(`[GameScene] base video LOAD ERROR: "${asset}"`);
    });
    this.load.start();
  }

  private _coverFitVideo(vid: Phaser.GameObjects.Video): void {
    const w = vid.width;
    const h = vid.height;
    if (w && h) vid.setScale(Math.max(1280 / w, 720 / h));
  }

  override update(_time: number, delta: number): void {
    if (this._baseScroll && this._base instanceof Phaser.GameObjects.TileSprite) {
      const dist = this._baseScroll.speed * (delta / 1000);
      if (this._baseScroll.dir === "vertical") {
        this._base.tilePositionY -= dist;
      } else {
        this._base.tilePositionX += dist;
      }
    }
  }

  // --- Object sprites (P4) ------------------------------------------------

  private _objectDepth(layer: string): number {
    return layer === "below" ? LAYERS.OBJECTS_BACK : LAYERS.OBJECTS_FRONT;
  }

  private _onObjectShow(id: string, asset: string, x: number, y: number, layer: string): void {
    if (!asset) return;
    const existing = this._objects.get(id);
    if (existing) existing.destroy();
    this._ensureLoaded(asset, () => {
      const px = (x / 100) * 1280;
      const py = (1 - y / 100) * 720; // y is % from the bottom edge
      const img = this.add
        .image(px, py, asset)
        .setOrigin(0.5, 1)
        .setScale(OBJECT_PIN_SCALE)
        .setDepth(this._objectDepth(layer));
      this._objects.set(id, img);
    });
  }

  private _onObjectUpdate(id: string, asset: string): void {
    const img = this._objects.get(id);
    if (!img || !asset) return;
    this._ensureLoaded(asset, () => {
      if (this._objects.get(id) === img) img.setTexture(asset); // re-skin in place
    });
  }

  private _onObjectHide(id: string): void {
    const img = this._objects.get(id);
    if (img) {
      img.destroy();
      this._objects.delete(id);
    }
  }

  private _clearObjects(): void {
    for (const o of this._objects.values()) o.destroy();
    this._objects.clear();
    this._finishPresentation(true);
  }

  /**
   * Lay out a room's ambient objects on its shelves (P5). Replaces all in-scene
   * object sprites with the deterministically-placed set. Textures are preloaded
   * before measuring widths (placement depends on every sprite's size), then the
   * pure ObjectPlacement module computes positions.
   * NOTE: widths use full texture size for now; Tier-1 alpha content-bounds
   * seating (so padded sprites don't float) is a follow-up.
   */
  private _onObjectRoom(items: Array<{ id: string; asset: string; scale: number }>, shelves: Shelf[]): void {
    for (const o of this._objects.values()) o.destroy();
    this._objects.clear();
    if (items.length === 0) return;

    const scaleById = new Map(items.map((it) => [it.id, it.scale]));
    const render = () => {
      const measured = items
        .filter((it) => this.textures.exists(it.asset))
        .map((it) => {
          const src = this.textures.get(it.asset).getSourceImage() as { width: number; height: number };
          return { id: it.id, width: src.width * it.scale, height: src.height * it.scale };
        });
      const assetById = new Map(items.map((it) => [it.id, it.asset]));
      for (const p of placeObjects(shelves, measured).placed) {
        const asset = assetById.get(p.id);
        if (!asset) continue;
        const img = this.add
          .image(p.x, p.y, asset)
          .setOrigin(0.5, 1)
          .setScale(scaleById.get(p.id) ?? OBJECT_PIN_SCALE)
          .setDepth(this._objectDepth(p.layer));
        this._objects.set(p.id, img);
      }
    };

    const toLoad = items.filter((it) => !this.textures.exists(it.asset));
    if (toLoad.length === 0) {
      render();
      return;
    }
    for (const it of toLoad) this.load.image(it.asset, resolveAssetUrl(it.asset));
    this.load.once("complete", render);
    this.load.start();
  }

  /**
   * Featured presentation (design note §9): dim everything except the object,
   * which zooms to centre-frame. The "dim sandwich" is a 50%-black rect one depth
   * band below the raised sprite; both sit below the React UI. Player-gated: the
   * caption shows via the normal dialogue/narration path, and the next `advance`
   * plays the exit (fade = into inventory, return/settle = back toward its place).
   */
  private _onObjectPresent(_id: string, asset: string, exit: string): void {
    if (!asset) return;
    this._finishPresentation(true); // a new presentation supersedes any active one
    this._ensureLoaded(asset, () => {
      const dim = this.add
        .rectangle(640, 360, 1280, 720, 0x000000, 1)
        .setDepth(LAYERS.OVERLAY)
        .setAlpha(0);
      const sprite = this.add
        .image(640, 360, asset)
        .setOrigin(0.5, 0.5)
        .setDepth(LAYERS.OVERLAY + 5);
      const target = (PRESENT_TARGET_H * 720) / (sprite.height || 720);
      sprite.setScale(target * 0.7).setAlpha(0.85);
      this.tweens.add({ targets: dim, alpha: PRESENT_DIM_ALPHA, duration: PRESENT_MS });
      this.tweens.add({
        targets: sprite,
        scaleX: target,
        scaleY: target,
        alpha: 1,
        duration: PRESENT_MS,
        ease: "Sine.easeOut",
      });
      this._presentation = { sprite, dim, exit };
    });
  }

  /** Player advanced past the caption → play the featured-presentation exit. */
  private _dismissPresentation(): void {
    this._finishPresentation(false);
  }

  private _finishPresentation(immediate: boolean): void {
    const p = this._presentation;
    if (!p) return;
    this._presentation = null;

    if (immediate) {
      p.sprite.destroy();
      p.dim.destroy();
      return;
    }

    const cleanup = () => { p.sprite.destroy(); p.dim.destroy(); };
    this.tweens.add({ targets: p.dim, alpha: 0, duration: PRESENT_EXIT_MS });
    if (p.exit === "fade") {
      this.tweens.add({ targets: p.sprite, alpha: 0, duration: PRESENT_EXIT_MS, onComplete: cleanup });
    } else {
      // "return"/"settle": shrink back toward its place while fading
      this.tweens.add({
        targets: p.sprite,
        scaleX: p.sprite.scaleX * 0.6,
        scaleY: p.sprite.scaleY * 0.6,
        alpha: 0,
        duration: PRESENT_EXIT_MS,
        ease: "Sine.easeIn",
        onComplete: cleanup,
      });
    }
  }

  private _onSpriteShow(id: string, asset: string, pos?: string, enter?: string, z?: number, scale?: number) {
    const position = POSITIONS[pos ?? "center"] ?? POSITIONS.center;

    this._ensureLoaded(asset, () => {
      const existing = this._sprites.get(id);
      if (existing) existing.destroy();

      const sprite = this.add
        .image(position.x, position.y, asset)
        .setOrigin(0.5, 1);
      sprite.setDepth(LAYERS.CHARACTERS + clampCharacterZ(z ?? 0));
      if (scale !== undefined) sprite.setScale(scale);

      if (enter === "fade_in") {
        sprite.setAlpha(0);
        this.tweens.add({
          targets: sprite,
          alpha: 1,
          duration: TRANSITION_MS,
        });
      } else if (enter === "slide_in") {
        const startX = position.x < 640 ? -200 : 1480;
        sprite.setX(startX);
        this.tweens.add({
          targets: sprite,
          x: position.x,
          duration: TRANSITION_MS,
          ease: "Power2",
        });
      } else if (enter === "zoom") {
        const targetScaleX = sprite.scaleX;
        const targetScaleY = sprite.scaleY;
        sprite.setAlpha(0).setScale(targetScaleX * 0.8, targetScaleY * 0.8);
        this.tweens.add({
          targets: sprite,
          alpha: 1,
          scaleX: targetScaleX,
          scaleY: targetScaleY,
          duration: TRANSITION_MS,
          ease: "Sine.easeOut",
        });
      } else if (enter === "slide_right") {
        sprite.setX(1480);
        this.tweens.add({ targets: sprite, x: position.x, duration: TRANSITION_MS, ease: "Power2" });
      } else if (enter === "slide_left") {
        sprite.setX(-200);
        this.tweens.add({ targets: sprite, x: position.x, duration: TRANSITION_MS, ease: "Power2" });
      } else if (enter === "slide_down") {
        sprite.setY(-360);
        this.tweens.add({ targets: sprite, y: position.y, duration: TRANSITION_MS, ease: "Power2" });
      } else if (enter === "slide_up") {
        sprite.setY(1080);
        this.tweens.add({ targets: sprite, y: position.y, duration: TRANSITION_MS, ease: "Power2" });
      }

      this._sprites.set(id, sprite);

      const queued = this._animationQueue.get(id);
      if (queued) {
        this._animationQueue.delete(id);
        for (const q of queued) {
          this._applyAnimation(sprite, q.anim, q.duration);
        }
      }
    });
  }

  private _onSpriteHide(id: string, exit?: string, duration?: number) {
    const sprite = this._sprites.get(id);
    if (!sprite) return;

    const ms = duration !== undefined ? duration * 1000 : TRANSITION_MS;

    const cleanup = () => {
      sprite.destroy();
      this._sprites.delete(id);
    };

    if (exit === "fade_out") {
      this.tweens.add({ targets: sprite, alpha: 0, duration: ms, onComplete: cleanup });
    } else if (exit === "slide_out") {
      const targetX = sprite.x < 640 ? -200 : 1480;
      this.tweens.add({ targets: sprite, x: targetX, duration: ms, ease: "Power2", onComplete: cleanup });
    } else if (exit === "slide_right") {
      this.tweens.add({ targets: sprite, x: 1480, duration: ms, ease: "Power2", onComplete: cleanup });
    } else if (exit === "slide_left") {
      this.tweens.add({ targets: sprite, x: -200, duration: ms, ease: "Power2", onComplete: cleanup });
    } else if (exit === "slide_down") {
      this.tweens.add({ targets: sprite, y: 1080, duration: ms, ease: "Power2", onComplete: cleanup });
    } else if (exit === "slide_up") {
      this.tweens.add({ targets: sprite, y: -360, duration: ms, ease: "Power2", onComplete: cleanup });
    } else if (exit === "zoom_out") {
      this.tweens.add({
        targets: sprite, alpha: 0,
        scaleX: sprite.scaleX * 0.8, scaleY: sprite.scaleY * 0.8,
        duration: ms, ease: "Sine.easeIn", onComplete: cleanup,
      });
    } else if (exit === "blur_fade") {
      this._blurFadeSprite(sprite, ms, cleanup);
    } else {
      cleanup();
    }
  }

  private _blurFadeSprite(
    sprite: Phaser.GameObjects.Image,
    ms: number,
    cleanup: () => void
  ): void {
    try {
      const blurFX = (sprite as unknown as { postFX?: { addBlur: (...a: unknown[]) => unknown } }).postFX?.addBlur(0, 2, 2, 1);
      if (blurFX) {
        this.tweens.add({
          targets: blurFX,
          strength: 6,
          duration: ms * 0.5,
          onComplete: () => {
            this.tweens.add({ targets: sprite, alpha: 0, duration: ms * 0.5, onComplete: cleanup });
          },
        });
        return;
      }
    } catch (_) {
      // postFX unavailable — fall through
    }
    this.tweens.add({ targets: sprite, alpha: 0, duration: ms, onComplete: cleanup });
  }

  private _onEffect(effect: string, intensity?: number, duration?: number, color?: string) {
    const ms = (duration ?? 0.3) * 1000;

    switch (effect) {
      case "screen_shake": {
        const str = intensity ?? 0.5;
        this.cameras.main.shake(ms, str * 0.01);
        break;
      }
      case "flash": {
        const alpha = intensity ?? 0.8;
        const col = color ? parseInt(color.replace("#", ""), 16) : 0xffffff;
        this._flashOverlay(col, alpha, ms);
        break;
      }
      case "fade_to_black": {
        this._fadeOverlay(0x000000, 0, 1, ms);
        break;
      }
      case "fade_from_black": {
        this._fadeOverlay(0x000000, 1, 0, ms);
        break;
      }
      case "fade_to_white": {
        this._fadeOverlay(0xffffff, 0, 1, ms);
        break;
      }
      case "fade_from_white": {
        this._fadeOverlay(0xffffff, 1, 0, ms);
        break;
      }
      case "vignette": {
        const alpha = intensity ?? 0.5;
        this._flashOverlay(0x000000, alpha, ms);
        break;
      }
      default:
        console.warn(`[GameScene] unknown effect: "${effect}"`);
    }
  }

  private _flashOverlay(color: number, alpha: number, ms: number) {
    if (this._effectOverlay) {
      this._effectOverlay.destroy();
      this._effectOverlay = null;
    }
    const rect = this.add.rectangle(640, 360, 1280, 720, color, alpha).setDepth(LAYERS.EFFECTS);
    this._effectOverlay = rect;
    this.tweens.add({
      targets: rect,
      alpha: 0,
      duration: ms,
      onComplete: () => {
        rect.destroy();
        if (this._effectOverlay === rect) this._effectOverlay = null;
      },
    });
  }

  private _fadeOverlay(color: number, fromAlpha: number, toAlpha: number, ms: number) {
    if (this._effectOverlay) {
      this._effectOverlay.destroy();
      this._effectOverlay = null;
    }
    const rect = this.add.rectangle(640, 360, 1280, 720, color, fromAlpha).setDepth(LAYERS.EFFECTS);
    this._effectOverlay = rect;
    this.tweens.add({
      targets: rect,
      alpha: toAlpha,
      duration: ms,
      onComplete: () => {
        if (toAlpha === 0) {
          rect.destroy();
          if (this._effectOverlay === rect) this._effectOverlay = null;
        }
      },
    });
  }

  private _onAnimation(id: string, anim: string, duration?: number) {
    const sprite = this._sprites.get(id);
    if (!sprite) {
      const queue = this._animationQueue.get(id) ?? [];
      queue.push({ anim, duration });
      this._animationQueue.set(id, queue);
      return;
    }
    this._applyAnimation(sprite, anim, duration);
  }

  private _applyAnimation(sprite: Phaser.GameObjects.Image, anim: string, duration?: number) {
    const ms = (duration ?? 0.5) * 1000;

    switch (anim) {
      case "laugh":
      case "shake": {
        const origX = sprite.x;
        this.tweens.add({
          targets: sprite,
          x: origX + 4,
          duration: 50,
          yoyo: true,
          repeat: Math.max(1, Math.floor(ms / 100) - 1),
          onComplete: () => { sprite.x = origX; },
        });
        break;
      }
      case "bounce": {
        const origY = sprite.y;
        this.tweens.add({
          targets: sprite,
          y: origY - 20,
          duration: ms / 2,
          yoyo: true,
          ease: "Sine.easeOut",
          onComplete: () => { sprite.y = origY; },
        });
        break;
      }
      case "nod": {
        const origY = sprite.y;
        this.tweens.add({
          targets: sprite,
          y: origY + 8,
          duration: ms / 2,
          yoyo: true,
          ease: "Sine.easeInOut",
          onComplete: () => { sprite.y = origY; },
        });
        break;
      }
      case "pulse": {
        const origScale = sprite.scaleX;
        this.tweens.add({
          targets: sprite,
          scaleX: origScale * 1.1,
          scaleY: origScale * 1.1,
          duration: ms / 2,
          yoyo: true,
          ease: "Sine.easeInOut",
        });
        break;
      }
      case "fade_out": {
        this.tweens.add({
          targets: sprite,
          alpha: 0,
          duration: ms,
        });
        break;
      }
      case "fade_in": {
        sprite.setAlpha(0);
        this.tweens.add({
          targets: sprite,
          alpha: 1,
          duration: ms,
        });
        break;
      }
      default:
        console.warn(`[GameScene] unknown animation: "${anim}"`);
    }
  }

  private _onMinigameStart(id: string, config: Record<string, unknown>): void {
    this._minigameActive = true;
    this.scene.pause("GameScene");

    const bgmDuck = (config.bgmDuck as number | undefined) ?? 0.3;
    if (this._audio) {
      this._audio.setChannelVolume?.("bgm", bgmDuck);
    }

    this.scene.launch("MinigameScene", {
      bus: this.bus,
      bb: this._bb,
      id,
      config,
    });
    this.scene.bringToTop("MinigameScene");
  }

  private _onMinigameComplete(): void {
    this._minigameActive = false;
    if (this._audio) {
      this._audio.setChannelVolume?.("bgm", 1.0);
    }
  }

  private _coverFit(img: Phaser.GameObjects.Image) {
    const scaleX = 1280 / img.width;
    const scaleY = 720 / img.height;
    img.setScale(Math.max(scaleX, scaleY));
  }

  shutdown() {
    if (this._rollback) {
      this._rollback.setAudioAdapter(null);
    }
    if (this._audio) {
      this._audio.shutdown();
      this._audio = null;
    }
    if (this._qKey) {
      this._qKey.removeAllListeners();
      this._qKey = null;
    }
    for (const unsub of this._unsubs) unsub();
    this._unsubs = [];
    for (const s of this._sprites.values()) s.destroy();
    this._sprites.clear();
    this._clearObjects();
    if (this._bg) {
      this._bg.destroy();
      this._bg = null;
    }
    this._onBaseClear();
    if (this._effectOverlay) {
      this._effectOverlay.destroy();
      this._effectOverlay = null;
    }
    this._animationQueue.clear();
  }
}
